import Decimal from 'decimal.js';
import { RefObject, useEffect, useMemo, useRef } from 'react';
import { SetRequired } from 'type-fest';

import {
  FullScreenModal,
  FullScreenModalProps,
} from '@/components/modals/FullScreenModal/FullScreenModal';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { AISuggestionsMatcherArrayField } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { useEntitySuggestionsContext } from '@/modules/aiSuggestions/context/EntitySuggestions.context';
import { useSelectedSuggestionEvidenceContext } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.context';
import { SelectedSuggestionEvidenceProvider } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.provider';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { DocumentChatPane } from '@/modules/chat/components/DocumentChatPane';
import {
  DocumentChatProvider,
  useOptionalDocumentChatContext,
} from '@/modules/chat/contexts/DocumentChatProvider';
import { DispositiveDiagramSyncProvider } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { DPFormRecipientTrackerProvider } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.provider';
import { useResetEvidenceOnDPEntry } from '@/modules/dispositiveProvisions/hooks/useResetEvidenceOnDPEntry';
import { SplitScreenLayoutWithDocuments } from '@/modules/documents/components/SplitScreenLayoutWithDocuments/SplitScreenLayoutWithDocuments';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { EditEntitySplitScreenModalProvider } from '@/modules/entities/EditEntitySplitScreen/contexts/EditEntitySplitScreenModal.provider';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenFooter } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFooter';
import { EditEntitySplitScreenHeader } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenHeader';
import { EditEntitySplitScreenRightContent } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenRightContent';
import { useImmediateOpenEditModal } from '@/modules/entities/hooks/useImmediateOpenEditModal';
import { FullScreenModalHouseholdChatHost } from '@/modules/household/chat/components/FullScreenModalHouseholdChatHost';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { useOptionalAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';

import { EntityType } from '../types/EntityType';

export type EditEntitySplitScreenModalProps = {
  entityType: EntityType;
  initialSection: EditEntitySection;
  // entityValueOverride is used when we're editing dispositive provisions from
  // a scenario where we have a projected entity value, like from a waterfall.
  // we show the projected value in the dispositive provisions section of the modal
  entityValueOverride?: Decimal;
  // Called after a successful save (any section). Hosts that derive their own
  // data from the entity (e.g. the presentation designer) use this to refetch
  // on save rather than on close.
  onAfterSave?: () => void;
} & EditEntitySplitScreenModalInnerProps;

type EditEntitySplitScreenModalInnerProps = {
  initialPrimaryClientId?: string;
} & SetRequired<Partial<FullScreenModalProps>, 'onClose'>;

function EditEntitySplitScreenModalInner({
  initialPrimaryClientId,
  ...modalProps
}: EditEntitySplitScreenModalInnerProps) {
  const { entityId } = useEntityDetailsContext();
  const { householdId, primaryClients } = useHouseholdDetailsContext();

  const {
    documentPanelProps: {
      activeDocumentId,
      activeDocumentViewerTab,
      annotations,
      activeAnnotationIndex,
      searchQuery,
      searchLoading,
    },
    setDocumentPanelProps,
  } = useSelectedSuggestionEvidenceContext();

  const { suggestionDocumentIds: entitySuggestionDocumentIds } =
    useEntitySuggestionsContext();

  const overrideDocumentIds = useMemo(() => {
    return entitySuggestionDocumentIds.length > 0
      ? entitySuggestionDocumentIds
      : undefined;
  }, [entitySuggestionDocumentIds]);

  const { isDPSection, isBDGSection, currentSection } =
    useEditEntitySplitScreenModalContext();

  // Clear the chat when the modal opens
  const chatContext = useOptionalDocumentChatContext();

  useEffect(() => {
    chatContext?.clearChat();

    // Only run when modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the active document with the chat context and clear chat history
  const pdfViewerContext = useOptionalAnnotatedPDFViewerContext();

  useEffect(() => {
    chatContext?.setDocumentId(activeDocumentId);
    chatContext?.clearChat();
    pdfViewerContext?.setAnnotations(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocumentId]);

  useResetEvidenceOnDPEntry(isDPSection);

  // Determine diagram primary client id when dispositions section is active
  let diagramPrimaryClientId = initialPrimaryClientId;

  if (isDPSection && !diagramPrimaryClientId && primaryClients?.length) {
    if (currentSection === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1) {
      diagramPrimaryClientId = primaryClients[0]?.id;
    } else if (
      currentSection === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2 &&
      primaryClients.length > 1
    ) {
      diagramPrimaryClientId = primaryClients[1]?.id;
    }
  }

  // BDG sections aren't keyed by grantor; default to the first primary
  // client so the diagram has an initial "who dies first" to render.
  if (isBDGSection && !diagramPrimaryClientId && primaryClients?.length) {
    diagramPrimaryClientId = primaryClients[0]?.id;
  }

  return (
    <FullScreenModal trowserKey={TrowserKey.EditEntity} {...modalProps}>
      <FullScreenModalHouseholdChatHost>
        {({ householdChatPane, isHouseholdChatReplacementEnabled }) => (
          <SplitScreenLayoutWithDocuments
            householdId={householdId}
            targetId={entityId!}
            documentIds={overrideDocumentIds}
            header={<EditEntitySplitScreenHeader />}
            rightContent={<EditEntitySplitScreenRightContent />}
            rightPane={
              isHouseholdChatReplacementEnabled ? (
                householdChatPane
              ) : (
                <DocumentChatPane documentId={activeDocumentId} />
              )
            }
            footer={<EditEntitySplitScreenFooter />}
            overrideActiveDocumentId={activeDocumentId}
            onUpdateActiveDocumentId={({
              documentId,
              shouldClearSearchResults,
            }) => {
              chatContext?.setDocumentId(documentId);
              chatContext?.clearChat();

              if (shouldClearSearchResults) {
                // Clear out the annotations and search-related props
                setDocumentPanelProps((prev) => ({
                  ...prev,
                  activeDocumentId: documentId,
                  searchQuery: '',
                  annotations: undefined,
                  activeAnnotationIndex: undefined,
                  searchLoading: false,
                }));
                return;
              }
              // Otherwise, only update the document id and tab
              setDocumentPanelProps((prev) => ({
                ...prev,
                activeDocumentId: documentId,
              }));
            }}
            overrideSearchQuery={searchQuery}
            overrideSearchLoading={searchLoading}
            overrideAnnotations={annotations}
            overrideActiveAnnotationIndex={activeAnnotationIndex}
            overrideDocumentViewerActiveTab={activeDocumentViewerTab}
            onUpdateDocumentViewerActiveTab={() => {
              // Unset the selected suggestion when the user switches between the document/summary views.
              // Preserve activeDocumentId so the chat pane remains functional.
              setDocumentPanelProps((prev) => ({
                ...prev,
                activeDocumentViewerTab: undefined,
                annotations: undefined,
                activeAnnotationIndex: undefined,
                searchQuery: '',
                searchLoading: false,
              }));
            }}
            initialPrimaryClientId={diagramPrimaryClientId}
            showDiagram={isDPSection || isBDGSection}
          />
        )}
      </FullScreenModalHouseholdChatHost>
    </FullScreenModal>
  );
}

export function EditEntitySplitScreenModal({
  entityType,
  initialSection,
  // entityValueOverride is used when we're editing dispositive provisions from
  // a scenario where we have a projected entity value, like from a waterfall.
  // we show the projected value in the dispositive provisions section of the modal
  entityValueOverride,
  onAfterSave,
  ...modalProps
}: EditEntitySplitScreenModalProps) {
  // The reference to the footer of the modal, where the cancel/save buttons
  // will be portaled to.
  const footerRef = useRef<HTMLDivElement>(null);
  // The reference to the header of the modal, where the section switcher
  // will be portaled to.
  const headerRef = useRef<HTMLDivElement>(null);

  // A map of the duplicate refs for each array field. This only includes
  // array fields that _might_ have duplicates.
  // The refs need to be initialized upfront, because they need to be passed
  // to the subforms.
  const duplicatesRefs = useRef<
    Partial<
      Record<AISuggestionsMatcherArrayField, RefObject<HTMLDivElement | null>>
    >
  >({
    'basicInformationSubform.grantors': useRef<HTMLDivElement | null>(null),
    'lifetimeBeneficiariesSubform.lifetimeBeneficiaries':
      useRef<HTMLDivElement | null>(null),
    'beneficiariesSubform.beneficiaries': useRef<HTMLDivElement | null>(null),
    'beneficiariesAfterBothGrantorsDie.beneficiaries':
      useRef<HTMLDivElement | null>(null),
    'trustDetailsSubform.trustees': useRef<HTMLDivElement | null>(null),
    'trustDetailsSubform.successorTrustees': useRef<HTMLDivElement | null>(
      null
    ),
    'trustDetailsSubform.trustAdvisors': useRef<HTMLDivElement | null>(null),
    'businessEntityDetailsSubform.owners': useRef<HTMLDivElement | null>(null),
    'businessEntityDetailsSubform.keyPeople': useRef<HTMLDivElement | null>(
      null
    ),
  });

  const { entityId } = useEntityDetailsContext();

  const { clearImmediateEditSearchParam } = useImmediateOpenEditModal();

  useEffect(() => {
    // This will clear out the immediate edit search param if it exists,
    // so that we don't keep re-triggering the modal to open.
    // The immediate edit search param gets set by the CreateEntityShortFormModal -> Add additional details button.
    clearImmediateEditSearchParam();
  }, [clearImmediateEditSearchParam]);

  return (
    <DocumentChatProvider>
      <HouseholdPermissionAwareDisabledProvider>
        <DispositiveDiagramSyncProvider>
          <DPFormRecipientTrackerProvider>
            <EditEntitySplitScreenModalProvider
              entityType={entityType}
              initialSection={initialSection}
              entityValueOverride={entityValueOverride}
              footerRef={footerRef}
              headerRef={headerRef}
              duplicatesRefs={duplicatesRefs.current}
              onClose={modalProps.onClose}
              onAfterSave={onAfterSave}
            >
              <SelectedSuggestionEvidenceProvider entityType={entityType}>
                <DispositiveProvisionSuggestionsProvider
                  targetId={entityId ?? ''}
                >
                  <EditEntitySplitScreenModalInner {...modalProps} />
                </DispositiveProvisionSuggestionsProvider>
              </SelectedSuggestionEvidenceProvider>
            </EditEntitySplitScreenModalProvider>
          </DPFormRecipientTrackerProvider>
        </DispositiveDiagramSyncProvider>
      </HouseholdPermissionAwareDisabledProvider>
    </DocumentChatProvider>
  );
}
